/**
 * A practice game against the engine, played entirely in this tab.
 *
 * No socket, no match id, no rating. That is the point: nothing here is
 * recorded, so taking a move back costs nothing and a learner can afford to try
 * the move they are unsure about. The moment practice touched a rating it would
 * become a thing to protect rather than a thing to experiment in.
 *
 * The reducer owns the board; the worker owns the thinking. Replies carry the
 * token of the position they were asked about, so a reply that lands after a
 * take-back is dropped rather than applied to a board it was never about.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { BOT_LEVELS, type BotLevel } from '../../../games/analysis/bot.ts';
import { readPosition, urgencyOf } from '../../../games/analysis/coach.ts';
import type { PositionCoaching } from '../../../games/analysis/coach.ts';
import { openingAdvice } from '../../../games/analysis/openings.ts';
import type { MoveReview } from '../../../games/analysis/review.ts';
import { applyMove, createInitial } from '../../../games/spec/interpreter.ts';
import type { SeatOf, State } from '../../../games/spec/interpreter.ts';
import type { GameSpec, RulesSpec } from '../../../games/spec/schema.ts';
import type { Seat } from '../../../shared/domain.ts';
import { QUIET_LINE, openingLine, urgencyLine } from './coachText.ts';
import type { CoachLine } from './coachText.ts';
import type { TurnRequest, TurnResponse } from './protocol.ts';

export type { BotLevel };
export { BOT_LEVELS };

type Rules = RulesSpec;

export interface PracticeState {
  readonly spec: GameSpec;
  readonly level: BotLevel;
  /** The seat the human is playing. */
  readonly yourSeat: Seat;
  readonly moves: readonly number[];
  readonly game: State<Rules>;
  /** True while the worker is judging and replying. */
  readonly thinking: boolean;
  /** Verdict on your most recent move. Cleared when you move again. */
  readonly lastReview: MoveReview | null;
  /** Whether the player has asked to be told the answer for this position. */
  readonly revealed: boolean;
  /** Rises every time the position changes, to void in-flight worker replies. */
  readonly token: number;
  readonly error: string | null;
}

type Action =
  | { readonly type: 'new_game'; readonly level: BotLevel; readonly youFirst: boolean }
  | { readonly type: 'set_level'; readonly level: BotLevel }
  | { readonly type: 'drop'; readonly param: number }
  | { readonly type: 'reveal' }
  | { readonly type: 'take_back' }
  | { readonly type: 'worker'; readonly response: TurnResponse };

/** A new practice game. Exported so tests can drive the reducer from move one. */
export function createPractice(
  spec: GameSpec,
  level: BotLevel = 'steady',
  youFirst = true,
): PracticeState {
  return freshGame(spec, level, youFirst, 1);
}

function freshGame(spec: GameSpec, level: BotLevel, youFirst: boolean, token: number): PracticeState {
  const first = spec.rules.seats[0] as SeatOf<Rules>;
  const second = (spec.rules.seats[1] ?? first) as SeatOf<Rules>;
  return {
    spec,
    level,
    yourSeat: (youFirst ? first : second) as Seat,
    moves: [],
    game: createInitial(spec.rules, first),
    // The bot opens when the player took second seat, so it is already thinking.
    thinking: !youFirst,
    lastReview: null,
    revealed: false,
    token,
    error: null,
  };
}

/** Apply a column to the game, or return the state unchanged if it is illegal. */
function drop(state: PracticeState, param: number): PracticeState {
  if (state.game.status.kind !== 'in_play') return state;
  const applied = applyMove(state.spec.rules, state.game, state.game.toMove, { param });
  if (!applied.ok) return state;
  return {
    ...state,
    game: applied.state,
    moves: [...state.moves, param],
    revealed: false,
    token: state.token + 1,
  };
}

export function practiceReducer(state: PracticeState, action: Action): PracticeState {
  switch (action.type) {
    case 'new_game':
      return freshGame(state.spec, action.level, action.youFirst, state.token + 1);

    case 'set_level':
      // Takes effect from the next bot move rather than restarting: changing
      // your mind about the difficulty should not cost you the position.
      return { ...state, level: action.level };

    case 'drop': {
      if (state.thinking || state.game.toMove !== state.yourSeat) return state;
      const moved = drop(state, action.param);
      if (moved === state) return state;
      // Cleared here rather than on the reply, so the previous verdict does not
      // sit under the board describing a move that is two moves old.
      return { ...moved, lastReview: null, thinking: moved.game.status.kind === 'in_play' };
    }

    case 'reveal':
      return { ...state, revealed: true };

    case 'take_back': {
      /*
       * Rewind to the last position where it was your turn.
       *
       * Pairs are removed rather than single moves: undoing only your own disc
       * would leave the bot's reply on a board it was never played against, and
       * the next verdict would be about a position that never occurred.
       */
      const rules = state.spec.rules;
      const first = rules.seats[0] as SeatOf<Rules>;
      let cut = state.moves.length;
      while (cut > 0) {
        cut -= 1;
        const candidate = state.moves.slice(0, cut);
        let replayed = createInitial(rules, first);
        let legal = true;
        for (const param of candidate) {
          const applied = applyMove(rules, replayed, replayed.toMove, { param });
          if (!applied.ok) {
            legal = false;
            break;
          }
          replayed = applied.state;
        }
        if (!legal) continue;
        if (replayed.status.kind === 'in_play' && replayed.toMove === state.yourSeat) {
          return {
            ...state,
            moves: candidate,
            game: replayed,
            lastReview: null,
            revealed: false,
            thinking: false,
            token: state.token + 1,
            error: null,
          };
        }
      }
      return state;
    }

    case 'worker': {
      const { response } = action;
      // A reply about a position the player has already left. Dropping it is
      // the whole reason the token exists.
      if (response.token !== state.token) return state;

      if (response.kind === 'failed') {
        return { ...state, thinking: false, error: response.message };
      }

      const withReview: PracticeState = {
        ...state,
        lastReview: response.review ?? state.lastReview,
        thinking: false,
      };
      if (response.botParam === null) return withReview;

      const played = drop(withReview, response.botParam);
      /*
       * A move that would not apply.
       *
       * It should be impossible — the worker chose it from a replay of these
       * same moves — but "impossible" here used to mean the position never
       * advanced while it stayed the bot's turn, so the effect asked again,
       * forever, each request running two searches until the tab died. Saying
       * so and stopping is survivable; spinning is not.
       */
      if (played === withReview) {
        return {
          ...withReview,
          error: `the coach tried to play column ${String(response.botParam + 1)}, which is not legal here`,
        };
      }

      // The bot's move advances the position, so the token moves with it; any
      // reply still in flight for the previous one is now correctly stale.
      return { ...played, lastReview: withReview.lastReview, thinking: false };
    }
  }
}

/** Everything the screen needs, derived rather than stored. */
export interface PracticeView {
  readonly spec: GameSpec;
  readonly level: BotLevel;
  readonly yourSeat: Seat;
  readonly board: readonly number[];
  readonly game: State<Rules>;
  readonly moves: readonly number[];
  readonly legalColumns: readonly number[];
  readonly lastMoveCell: number | null;
  readonly winningLine: readonly number[] | null;
  readonly yourTurn: boolean;
  readonly thinking: boolean;
  readonly over: boolean;
  /** Set once the game ends: whether you won, lost or drew. */
  readonly result: 'win' | 'loss' | 'draw' | null;
  readonly coaching: PositionCoaching;
  /** The one thing worth saying about this position. */
  readonly line: CoachLine;
  readonly revealed: boolean;
  readonly lastReview: MoveReview | null;
  readonly canTakeBack: boolean;
  readonly error: string | null;
}

/** The cell the most recent disc landed in, for the board's highlight. */
function lastCell(spec: GameSpec, moves: readonly number[]): number | null {
  if (moves.length === 0) return null;
  const rules = spec.rules;
  const first = rules.seats[0] as SeatOf<Rules>;
  let state = createInitial(rules, first);
  let cell: number | null = null;
  for (const param of moves) {
    const applied = applyMove(rules, state, state.toMove, { param });
    if (!applied.ok) break;
    cell = applied.primary;
    state = applied.state;
  }
  return cell;
}

export interface UsePracticeOptions {
  readonly spec: GameSpec;
  readonly level?: BotLevel;
  readonly youFirst?: boolean;
  /**
   * Runs the engine. Defaults to a real `Worker`; tests and environments
   * without workers pass their own.
   */
  readonly createWorker?: () => Worker;
}

export interface PracticeControls {
  readonly view: PracticeView;
  readonly drop: (param: number) => void;
  readonly reveal: () => void;
  readonly takeBack: () => void;
  readonly newGame: (level: BotLevel, youFirst: boolean) => void;
  readonly setLevel: (level: BotLevel) => void;
}

export function usePractice(options: UsePracticeOptions): PracticeControls {
  const { spec } = options;
  const [state, dispatch] = useReducer(
    practiceReducer,
    undefined,
    () => freshGame(spec, options.level ?? 'steady', options.youFirst ?? true, 1),
  );

  const workerRef = useRef<Worker | null>(null);
  const create = options.createWorker;

  useEffect(() => {
    const worker = create
      ? create()
      : new Worker(new URL('./coach.worker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;

    const onMessage = (event: MessageEvent<TurnResponse>) => {
      dispatch({ type: 'worker', response: event.data });
    };
    worker.addEventListener('message', onMessage);

    return () => {
      worker.removeEventListener('message', onMessage);
      worker.terminate();
      workerRef.current = null;
    };
  }, [create]);

  /**
   * The position the engine has already been asked about.
   *
   * One request per position, however many times the component re-renders for
   * unrelated reasons — revealing a hint or changing the difficulty must not
   * queue a second search behind the one already running.
   */
  const askedFor = useRef(-1);

  /*
   * Ask the worker whenever it is the bot's turn.
   *
   * Driven off the position rather than fired from the click handler, so the
   * bot also moves when it has the opening move and after a take-back that
   * happens to land on its turn. One effect, one rule: if it is not your turn
   * and the game is live, the engine is thinking.
   */
  useEffect(() => {
    const worker = workerRef.current;
    if (!worker) return;
    if (state.game.status.kind !== 'in_play') return;
    if (state.game.toMove === state.yourSeat) return;
    if (state.error !== null) return;
    if (askedFor.current === state.token) return;
    askedFor.current = state.token;

    const request: TurnRequest = {
      kind: 'turn',
      rules: state.spec.rules,
      firstSeat: state.spec.rules.seats[0] as number,
      params: state.moves,
      level: state.level,
      judgeLast: state.moves.length > 0,
      token: state.token,
    };
    worker.postMessage(request);
  }, [state.game, state.moves, state.token, state.level, state.spec, state.yourSeat, state.error]);

  const view = useMemo<PracticeView>(() => {
    const rules = state.spec.rules;
    const coaching = readPosition(rules, state.game);
    const status = state.game.status;
    const over = status.kind !== 'in_play';
    const yourTurn = !over && state.game.toMove === state.yourSeat && !state.thinking;

    /*
     * Advice is for your turn only.
     *
     * While the bot is thinking the position on screen is one you cannot act
     * on, and a panel that kept coaching it would be teaching a move nobody is
     * allowed to make.
     */
    const urgency = yourTurn ? urgencyOf(coaching) : null;
    const opening = yourTurn ? openingAdvice(rules, state.game) : null;
    const line: CoachLine = urgency
      ? urgencyLine(urgency)
      : opening
        ? openingLine(opening)
        : QUIET_LINE;

    const result =
      status.kind === 'win'
        ? status.winner === state.yourSeat
          ? ('win' as const)
          : ('loss' as const)
        : status.kind === 'draw'
          ? ('draw' as const)
          : null;

    return {
      spec: state.spec,
      level: state.level,
      yourSeat: state.yourSeat,
      board: state.game.board as readonly number[],
      game: state.game,
      moves: state.moves,
      legalColumns: coaching.legal,
      lastMoveCell: lastCell(state.spec, state.moves),
      winningLine: status.kind === 'win' ? status.line : null,
      yourTurn,
      thinking: state.thinking,
      over,
      result,
      coaching,
      line,
      revealed: state.revealed,
      lastReview: state.lastReview,
      // Something to undo, and a position of your own to go back to.
      canTakeBack: state.moves.length > 0 && !state.thinking,
      error: state.error,
    };
  }, [state]);

  return {
    view,
    drop: useCallback((param: number) => dispatch({ type: 'drop', param }), []),
    reveal: useCallback(() => dispatch({ type: 'reveal' }), []),
    takeBack: useCallback(() => dispatch({ type: 'take_back' }), []),
    newGame: useCallback(
      (level: BotLevel, youFirst: boolean) => dispatch({ type: 'new_game', level, youFirst }),
      [],
    ),
    setLevel: useCallback((level: BotLevel) => dispatch({ type: 'set_level', level }), []),
  };
}
